import * as React from "react";
import PropTypes from "prop-types";
import {
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { landingRegistry, defaultLanding } from "../../registries/landingRegistry";

export default function LandingShell({
  appName = "Abra Controls Directive 83",
  heroTitle = "FPZ Reporting Built for Repeatable Workflows",
  heroSubtitle = "Generate, save, share, analyze, and create work orders.",
  heroImageUrl,
  primaryCta = { label: "Get Started", href: "/sign-in" },
  secondaryCta = { label: "Learn More", href: "#features" },
  children,
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100dvh" }}>
      <CssBaseline />

      {/* Top Nav */}
      <AppBar
        position="absolute"
        elevation={0}
        sx={{
          bgcolor: "transparent",
          color: "common.white",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight={800}>
            {appName}
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button
              color="inherit"
              href={secondaryCta.href}
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
            >
              {secondaryCta.label}
            </Button>
            <Button variant="contained" color="primary" href={primaryCta.href}>
              {primaryCta.label}
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          height: { xs: "90vh", md: "100vh" },
          minHeight: 600,
          display: "flex",
          alignItems: "center",
          color: "common.white",
          overflow: "visible",
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: heroImageUrl
              ? `url(${heroImageUrl})`
              : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: heroImageUrl ? "brightness(0.55)" : "none",
          }}
        />

        {/* Hero Content */}
        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Typography
              variant={isDesktop ? "h2" : "h3"}
              fontWeight={900}
              sx={{ letterSpacing: -1, mb: 2 }}
            >
              {heroTitle}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                opacity: 0.9,
                mb: 4,
              }}
            >
              {heroSubtitle}
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                href={primaryCta.href}
              >
                {primaryCta.label}
              </Button>

              <Button
                variant="outlined"
                size="large"
                href={secondaryCta.href}
                sx={{
                  color: "common.white",
                  borderColor: "rgba(255,255,255,0.7)",
                  "&:hover": {
                    borderColor: "common.white",
                    bgcolor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                {secondaryCta.label}
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Content Section Below Hero */}
      <Box
        id="features"
        component="section"
        sx={{
          position: "relative",
          mt: { xs: -10, md: -13 },
          pt: 0,
          pb: { xs: 6, md: 10 },
          bgcolor: "transparent",
          zIndex: 2,
        }}
      >
        <Container maxWidth="lg">
          {children}
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} {appName}
          </Typography>
          {/*
          <Stack direction="row" spacing={3}>
            <Typography
              component="a"
              href="/privacy"
              variant="body2"
              color="text.secondary"
              sx={{ textDecoration: "none" }}
            >
              Privacy
            </Typography>
            <Typography
              component="a"
              href="/terms"
              variant="body2"
              color="text.secondary"
              sx={{ textDecoration: "none" }}
            >
              Terms
            </Typography>
          </Stack>
          */}
        </Container>
      </Box>
    </Box>
  );
}

LandingShell.propTypes = {
  appName: PropTypes.string,
  heroTitle: PropTypes.string,
  heroSubtitle: PropTypes.string,
  heroImageUrl: PropTypes.string,
  primaryCta: PropTypes.shape({
    label: PropTypes.string,
    href: PropTypes.string,
  }),
  secondaryCta: PropTypes.shape({
    label: PropTypes.string,
    href: PropTypes.string,
  }),
  children: PropTypes.node,
};
